import { Student, PaymentRecord, AttendanceRecord, TeacherAttendanceRecord, GradeRecord, ClassInfo, Subject, SchoolYear, Teacher } from '../types';

class StorageManager {
  private readonly STUDENTS_KEY = 'students';
  private readonly PAYMENTS_KEY = 'payments';
  private readonly ATTENDANCE_KEY = 'attendance';
  private readonly TEACHER_ATTENDANCE_KEY = 'teacher_attendance';
  private readonly GRADES_KEY = 'grades';
  private readonly CLASSES_KEY = 'classes';
  private readonly SUBJECTS_KEY = 'subjects';
  private readonly CUSTOM_REPORTS_KEY = 'custom_reports';
  private readonly ALERT_RULES_KEY = 'alert_rules';
  private readonly PREFERENCES_KEY = 'preferences';
  private readonly SCHOOL_YEARS_KEY = 'schoolYears';
  private readonly CONTEXT_KEY = 'current_context';
  private readonly TEACHERS_KEY = 'teachers';
  private readonly CUSTOM_EXAMS_KEY = 'custom_exams';

  // 为外部调试工具提供所有键名
  static readonly STORAGE_KEYS = {
    STUDENTS: 'students',
    PAYMENTS: 'payments',
    ATTENDANCE: 'attendance',
    TEACHER_ATTENDANCE: 'teacher_attendance',
    GRADES: 'grades',
    CLASSES: 'classes',
    SUBJECTS: 'subjects',
    CUSTOM_REPORTS: 'custom_reports',
    ALERT_RULES: 'alert_rules',
    PREFERENCES: 'preferences',
    SCHOOL_YEARS: 'schoolYears',
    CONTEXT: 'current_context',
    TEACHERS: 'teachers',
    CUSTOM_EXAMS: 'custom_exams'
  };

  // 当前上下文
  private currentSchoolYearId: string | null = null;
  private currentTermId: string | null = null;

  constructor() {
    // 初始化时尝试从localStorage加载上下文
    console.log("StorageManager initializing...");
    this.loadContext();
    console.log(`Initial context: SchoolYear=${this.currentSchoolYearId}, Term=${this.currentTermId}`);
  }

  // 加载上下文
  private loadContext() {
    try {
      const savedContext = localStorage.getItem(this.CONTEXT_KEY);
      if (savedContext) {
        const { schoolYearId, termId } = JSON.parse(savedContext);
        this.currentSchoolYearId = schoolYearId;
        this.currentTermId = termId;
        console.log(`Context loaded from storage: SchoolYear=${schoolYearId}, Term=${termId}`);
      } else {
        // 如果没有保存的上下文，尝试使用当前活跃学年的活动学期
        const activeYear = this.getCurrentSchoolYear();
        if (activeYear) {
          this.currentSchoolYearId = activeYear.id;
          
          // 先检查是否有活动学期
          if (activeYear.terms && activeYear.terms.length > 0) {
            const activeTerm = activeYear.terms.find(t => t.isActive);
            if (activeTerm) {
              this.currentTermId = activeTerm.id;
            } else {
              // 如果没有活动学期，使用第一个学期
              this.currentTermId = activeYear.terms[0].id;
            }
            this.saveContext();
            console.log(`Context set to active year and term: SchoolYear=${this.currentSchoolYearId}, Term=${this.currentTermId}`);
          }
        }
      }
      
      // 如果成功加载了上下文，触发上下文变更事件
      if (this.currentSchoolYearId && this.currentTermId) {
        const event = new CustomEvent('contextChange', { 
          detail: { 
            schoolYearId: this.currentSchoolYearId, 
            termId: this.currentTermId 
          } 
        });
        setTimeout(() => {
          window.dispatchEvent(event);
          console.log('Context change event dispatched during initialization');
        }, 100); // 延迟触发事件，确保组件已经挂载
      }
    } catch (error) {
      console.error('Failed to load context:', error);
    }
  }

  // 保存上下文到localStorage
  private saveContext() {
    try {
      localStorage.setItem(this.CONTEXT_KEY, JSON.stringify({
        schoolYearId: this.currentSchoolYearId,
        termId: this.currentTermId
      }));
    } catch (error) {
      console.error('Failed to save context:', error);
    }
  }

  // 设置当前上下文
  setCurrentContext(schoolYearId: string, termId: string) {
    console.log(`[StorageManager] 设置上下文: 旧值(schoolYearId=${this.currentSchoolYearId}, termId=${this.currentTermId}), 新值(schoolYearId=${schoolYearId}, termId=${termId})`);
    
    // 检查是否有实际变化
    const hasChanged = this.currentSchoolYearId !== schoolYearId || this.currentTermId !== termId;
    if (!hasChanged) {
      console.log(`[StorageManager] 上下文未变化，跳过更新`);
      return;
    }
    
    // 更新内部状态
    this.currentSchoolYearId = schoolYearId;
    this.currentTermId = termId;
    
    // 保存到localStorage
    this.saveContext();
    
    // 在触发上下文变更事件前清除所有实体缓存，确保组件能获取到最新数据
    this.clearAllCache();
    console.log(`[StorageManager] 上下文已变更，已清除所有缓存`);
    
    // 触发一个事件，通知所有组件上下文已变更
    const event = new CustomEvent('contextChange', { 
      detail: { schoolYearId, termId } 
    });
    
    console.log(`[StorageManager] 触发contextChange事件: schoolYearId=${schoolYearId}, termId=${termId}`);
    window.dispatchEvent(event);
    
    // 为了确保事件能够被所有已注册的监听器捕获，添加一个延迟触发
    setTimeout(() => {
      console.log(`[StorageManager] 延迟触发contextChange事件: schoolYearId=${schoolYearId}, termId=${termId}`);
      window.dispatchEvent(new CustomEvent('contextChange', { 
        detail: { schoolYearId, termId } 
      }));
    }, 100);
    
    console.log(`[StorageManager] 上下文已更新: School Year ID=${schoolYearId}, Term ID=${termId}`);
  }

  // 获取当前上下文
  getCurrentContext() {
    // 检查是否为只读模式
    const isReadOnly = !this.canModifyData();
    
    return {
      schoolYearId: this.currentSchoolYearId,
      termId: this.currentTermId,
      isReadOnly
    };
  }

  // 判断当前是否处于只读模式（非当前活动学年或学期）
  isReadOnlyMode(): boolean {
    // 如果没有设置任何上下文（学年或学期），视为只读模式
    if (!this.currentSchoolYearId || !this.currentTermId) {
      console.log('[ReadOnlyMode] 未设置完整上下文，启用只读模式');
      return true;
    }
    
    // 检查选择的学期是否是活动学期
    const schoolYears = this.getSchoolYears();
    const currentYear = schoolYears.find(y => y.id === this.currentSchoolYearId);
    
    if (!currentYear) {
      console.log('[ReadOnlyMode] 选择的学年不存在，启用只读模式');
      return true;
    }
    
    // 检查当前学年是否是活动学年
    if (!currentYear.isActive) {
      console.log(`[ReadOnlyMode] 当前选择的学年 (${currentYear.name}) 不是活动学年，启用只读模式`);
      return true;
    }
    
    if (currentYear.terms) {
      const currentTerm = currentYear.terms.find(t => t.id === this.currentTermId);
      
      // 如果当前选择的学期不是活动学期，启用只读模式
      if (currentTerm && !currentTerm.isActive) {
        console.log(`[ReadOnlyMode] 当前选择的学期 (${currentTerm.name}) 不是活动学期，启用只读模式`);
        return true;
      }
    }
    
    return false;
  }

  // 判断是否有当前有效上下文（学年和学期都设置）
  hasValidContext() {
    const isValid = Boolean(this.currentSchoolYearId && this.currentTermId);
    console.log(`[DEBUG] 检查上下文是否完全有效: ${isValid}, schoolYearId=${this.currentSchoolYearId}, termId=${this.currentTermId}`);
    return isValid;
  }

  // 判断是否至少有学年上下文
  hasSchoolYearContext() {
    const hasYear = Boolean(this.currentSchoolYearId);
    console.log(`[DEBUG] 检查是否有学年上下文: ${hasYear}, schoolYearId=${this.currentSchoolYearId}`);
    return hasYear;
  }

  // 根据上下文情况进行数据筛选的通用方法
  private filterDataByContext<T extends {schoolYearId?: string, termId?: string}>(
    allData: T[],
    entityName: string
  ): T[] {
    // 没有任何上下文（学年和学期都没有），返回空数组
    if (!this.currentSchoolYearId) {
      console.log(`[DEBUG] 未设置学年，返回空${entityName}数据`);
      return [];
    }
    
    // 有学年但没有学期，只按学年筛选
    if (this.currentSchoolYearId && !this.currentTermId) {
      console.log(`[DEBUG] 只有学年上下文，按学年筛选${entityName}`);
      const yearFiltered = allData.filter(item => 
        item.schoolYearId === this.currentSchoolYearId || 
        (!item.schoolYearId && false) // 不包含没有学年标记的旧数据
      );
      console.log(`[DEBUG] 按学年筛选后${entityName}数量: ${yearFiltered.length}`);
      return yearFiltered;
    }
    
    // 有完整上下文（学年和学期），先按学年后按学期筛选
    console.log(`[DEBUG] 有完整上下文，按学年和学期筛选${entityName}`);
    const yearFiltered = allData.filter(item => 
      item.schoolYearId === this.currentSchoolYearId || 
      (!item.schoolYearId && false) // 不包含没有学年标记的旧数据
    );
    console.log(`[DEBUG] 按学年筛选后${entityName}数量: ${yearFiltered.length}`);
    
    const termFiltered = yearFiltered.filter(item => 
      item.termId === this.currentTermId || 
      (!item.termId && false) // 不包含没有学期标记的旧数据
    );
    console.log(`[DEBUG] 按学年和学期筛选后${entityName}数量: ${termFiltered.length}`);
    
    // 确保返回所有符合条件的数据，不做任何限制
    return termFiltered;
  }

  // Students
  getStudents(skipCache: boolean = false): Student[] {
    const allStudents = JSON.parse(localStorage.getItem(this.STUDENTS_KEY) || '[]') as Student[];
    
    // 如果请求跳过缓存，直接筛选并返回结果
    if (skipCache) {
      console.log(`[DEBUG] 跳过缓存，直接筛选学生数据`);
      const result = this.filterDataByContext(allStudents, '学生');
      console.log(`[DEBUG] 直接筛选后学生数量: ${result.length}`);
      return result;
    }
    
    // 使用缓存优化频繁的学生数据获取
    const cacheKey = `${this.currentSchoolYearId}-${this.currentTermId}-students`;
    const cachedResult = sessionStorage.getItem(cacheKey);
    
    if (cachedResult) {
      try {
        const parsed = JSON.parse(cachedResult);
        console.log(`[DEBUG] 从缓存获取学生数据: ${parsed.length}条`);
        return parsed;
      } catch (e) {
        console.error('学生数据缓存解析失败:', e);
        // 继续使用下面的方法
      }
    }
    
    // 使用筛选函数并确保返回所有匹配记录
    const result = this.filterDataByContext(allStudents, '学生');
    console.log(`[DEBUG] 未找到缓存，筛选后学生数量: ${result.length}`);
    
    // 缓存结果
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify(result));
    } catch (e) {
      console.error('缓存学生数据失败:', e);
    }
    
    return result;
  }

  saveStudent(student: Student): boolean {
    // 检查是否允许修改数据
    if (!this.canModifyData(student)) {
      alert('不能修改非当前学年学期的数据');
      return false;
    }
    
    student = this.ensureContextFields(student); // 确保上下文字段存在
    
    const students = JSON.parse(localStorage.getItem(this.STUDENTS_KEY) || '[]') as Student[];
    const index = students.findIndex((s: Student) => s.id === student.id);
    const currentTime = new Date().toISOString();
    
    if (index >= 0) {
      // 更新现有学生，保留创建时间但更新修改时间
      student.updated_at = currentTime;
      student.last_modified_locally = currentTime;
      if (!student.created_at) {
        student.created_at = currentTime;
      }
      students[index] = student;
    } else {
      // 新建学生，设置创建和修改时间
      student.created_at = currentTime;
      student.updated_at = currentTime;
      student.last_modified_locally = currentTime;
      students.push(student);
    }
    localStorage.setItem(this.STUDENTS_KEY, JSON.stringify(students));
    
    // 清除相关缓存
    this.clearEntityCache('students');
    
    return true;
  }
  
  deleteStudent(studentId: string): boolean {
    // 🔥 修复：从全局数据中删除，而不是只从当前上下文删除
    const allStudents = JSON.parse(localStorage.getItem(this.STUDENTS_KEY) || '[]') as Student[];
    const studentToDelete = allStudents.find(s => s.id === studentId);
    
    // 检查是否有权限修改数据
    if (!studentToDelete || !this.canModifyData(studentToDelete)) {
      console.error('[StorageManager] 删除学生失败：没有权限修改非当前学年学期的数据');
      return false;
    }
    
    // 🔥 修复：从全局数据中删除指定学生，保留其他学年学期的学生数据
    const updatedAllStudents = allStudents.filter(s => s.id !== studentId);
    localStorage.setItem(this.STUDENTS_KEY, JSON.stringify(updatedAllStudents));
    
    // 清除相关缓存
    this.clearEntityCache('students');
    
    console.log(`[StorageManager] 成功删除学生: ${studentToDelete.name} (ID: ${studentId})`);
    console.log(`[StorageManager] 剩余学生总数: ${updatedAllStudents.length}`);
    
    return true;
  }

  // School Years
  getSchoolYears(): SchoolYear[] {
    try {
      const data = localStorage.getItem(this.SCHOOL_YEARS_KEY) || '[]';
      const parsedData = JSON.parse(data);
      // 确保返回数组
      if (!Array.isArray(parsedData)) {
        console.error(`[StorageManager] 学年数据不是数组格式:`, parsedData);
        return [];
      }
      return parsedData;
    } catch (error) {
      console.error(`[StorageManager] 获取学年数据时出错:`, error);
      return [];
    }
  }

  saveSchoolYear(schoolYear: SchoolYear) {
    const schoolYears = this.getSchoolYears();
    const index = schoolYears.findIndex(y => y.id === schoolYear.id);
    
    // 如果要将此学年设置为活动学年，先将其他所有学年设置为非活动
    if (schoolYear.isActive) {
      schoolYears.forEach(year => {
        if (year.id !== schoolYear.id) {
          year.isActive = false;
        }
      });
    }
    
    if (index >= 0) {
      schoolYears[index] = schoolYear;
    } else {
      schoolYears.push(schoolYear);
    }
    localStorage.setItem(this.SCHOOL_YEARS_KEY, JSON.stringify(schoolYears));
    
    // 如果这是第一个学年，或者这是新设置的活动学年，自动设置为当前上下文
    if (schoolYears.length === 1 || schoolYear.isActive) {
      if (schoolYear.terms && schoolYear.terms.length > 0) {
        // 优先使用标记为活动的学期
        const activeTerm = schoolYear.terms.find(term => term.isActive);
        if (activeTerm) {
          this.setCurrentContext(schoolYear.id, activeTerm.id);
        } else {
          // 如果没有活动学期，使用第一个学期
          this.setCurrentContext(schoolYear.id, schoolYear.terms[0].id);
        }
      }
    }
  }

  deleteSchoolYear(schoolYearId: string) {
    const schoolYears = this.getSchoolYears();
    const newSchoolYears = schoolYears.filter(y => y.id !== schoolYearId);
    localStorage.setItem(this.SCHOOL_YEARS_KEY, JSON.stringify(newSchoolYears));
    
    // 如果删除的是当前上下文的学年，重置上下文
    if (this.currentSchoolYearId === schoolYearId) {
      const activeYear = this.getCurrentSchoolYear();
      if (activeYear && activeYear.terms && activeYear.terms.length > 0) {
        this.setCurrentContext(activeYear.id, activeYear.terms[0].id);
      } else {
        this.currentSchoolYearId = null;
        this.currentTermId = null;
        this.saveContext();
      }
    }
  }

  getCurrentSchoolYear(): SchoolYear | null {
    const schoolYears = this.getSchoolYears();
    // 先尝试找到被标记为活动的学年
    let activeYear = schoolYears.find(y => y.isActive);
    
    // 如果没有找到活动学年，尝试找到当前日期所在的学年
    if (!activeYear) {
      const currentDate = new Date().toISOString().split('T')[0];
      activeYear = schoolYears.find(y => 
        y.startDate <= currentDate && y.endDate >= currentDate
      );
    }
    
    return activeYear || null;
  }

  getCurrentTerm(): { termId: string, termName: string } | null {
    // 如果有当前上下文，直接使用
    if (this.currentSchoolYearId && this.currentTermId) {
      const schoolYear = this.getSchoolYears().find(y => y.id === this.currentSchoolYearId);
      if (!schoolYear || !schoolYear.terms) {
        return null;
      }
      
      const term = schoolYear.terms.find(t => t.id === this.currentTermId);
      if (!term) {
        return null;
      }
      
      return {
        termId: term.id,
        termName: term.name
      };
    }
    
    // 如果没有当前上下文，尝试找到活动学年的活动学期
    const activeYear = this.getCurrentSchoolYear();
    if (!activeYear || !activeYear.terms || activeYear.terms.length === 0) {
      return null;
    }
    
    // 先查找活动学期
    const activeTerm = activeYear.terms.find(t => t.isActive);
    if (activeTerm) {
      // 顺便更新当前上下文
      this.setCurrentContext(activeYear.id, activeTerm.id);
      return {
        termId: activeTerm.id,
        termName: activeTerm.name
      };
    }
    
    // 如果没有活动学期，使用第一个学期
    const firstTerm = activeYear.terms[0];
    // 顺便更新当前上下文
    this.setCurrentContext(activeYear.id, firstTerm.id);
    return {
      termId: firstTerm.id,
      termName: firstTerm.name
    };
  }

  // 确保记录有学年学期标记
  private ensureContextFields<T extends {schoolYearId?: string, termId?: string, id?: string, name?: string}>(record: T): T {
    // 记录之前的值，用于调试
    const originalYearId = record.schoolYearId;
    const originalTermId = record.termId;
    
    // 如果有学年上下文但记录没有指定学年，则设置学年
    if (this.currentSchoolYearId && !record.schoolYearId) {
      record.schoolYearId = this.currentSchoolYearId || undefined;
      console.log(`[DEBUG] 为记录设置学年: id=${record.id}, name=${record.name}, 学年ID=${record.schoolYearId}`);
    }
    
    // 如果有完整上下文（学年和学期）且记录没有指定学期，则设置学期
    if (this.hasValidContext() && !record.termId && this.currentTermId) {
      record.termId = this.currentTermId;
      console.log(`[DEBUG] 为记录设置学期: id=${record.id}, name=${record.name}, 学期ID=${record.termId}`);
    }
    
    // 如果发生了变化，记录调试信息
    if (originalYearId !== record.schoolYearId || originalTermId !== record.termId) {
      console.log(`[DEBUG] 记录上下文已更新: id=${record.id}, name=${record.name}, 
        schoolYearId: ${originalYearId} => ${record.schoolYearId}, 
        termId: ${originalTermId} => ${record.termId}`);
    }
    
    return record;
  }

  // Payments
  getPayments(skipCache: boolean = false): PaymentRecord[] {
    const allPayments = JSON.parse(localStorage.getItem(this.PAYMENTS_KEY) || '[]') as PaymentRecord[];
    
    // 如果请求跳过缓存，直接筛选并返回结果
    if (skipCache) {
      console.log(`[DEBUG] 跳过缓存，直接筛选缴费数据`);
      const result = this.filterDataByContext(allPayments, '支付');
      console.log(`[DEBUG] 直接筛选后缴费数量: ${result.length}`);
      return result;
    }
    
    // 使用缓存优化频繁的缴费数据获取
    const cacheKey = `${this.currentSchoolYearId}-${this.currentTermId}-payments`;
    const cachedResult = sessionStorage.getItem(cacheKey);
    
    if (cachedResult) {
      try {
        const parsed = JSON.parse(cachedResult);
        console.log(`[DEBUG] 从缓存获取缴费数据: ${parsed.length}条`);
        return parsed;
      } catch (e) {
        console.error('缴费数据缓存解析失败:', e);
        // 如果解析失败，继续使用原始方法
      }
    }
    
    // 如果没有缓存，使用原来的过滤方法
    const result = this.filterDataByContext(allPayments, '支付');
    console.log(`[DEBUG] 未找到缓存，筛选后缴费数量: ${result.length}`);
    
    // 缓存结果到 sessionStorage (仅缓存当前会话)
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify(result));
    } catch (e) {
      console.error('缓存缴费数据失败:', e);
      // 缓存失败不影响主要功能，只记录错误
    }
    
    return result;
  }

  savePayment(payment: PaymentRecord) {
    // 检查是否允许修改数据
    if (!this.canModifyData(payment)) {
      alert('不能修改非当前学年学期的数据');
      return false;
    }
    
    payment = this.ensureContextFields(payment); // 确保上下文字段存在
    
    const payments = JSON.parse(localStorage.getItem(this.PAYMENTS_KEY) || '[]');
    const index = payments.findIndex((p: PaymentRecord) => p.id === payment.id);
    const currentTime = new Date().toISOString();
    
    if (index >= 0) {
      // 更新现有支付记录，保留创建时间但更新修改时间
      payment.updated_at = currentTime;
      payment.last_modified_locally = currentTime;
      if (!payment.created_at) {
        payment.created_at = currentTime;
      }
      payments[index] = payment;
    } else {
      // 新建支付记录，设置创建和修改时间
      payment.created_at = currentTime;
      payment.updated_at = currentTime;
      payment.last_modified_locally = currentTime;
      payments.push(payment);
    }
    localStorage.setItem(this.PAYMENTS_KEY, JSON.stringify(payments));
    return true;
  }

  deletePayment(id: string) {
    // 先获取原始记录
    const payments = JSON.parse(localStorage.getItem(this.PAYMENTS_KEY) || '[]') as PaymentRecord[];
    const paymentToDelete = payments.find(p => p.id === id);
    
    // 检查是否允许删除
    if (paymentToDelete && !this.canModifyData(paymentToDelete)) {
      alert('不能删除非当前学年学期的数据');
      return false;
    }
    
    const updatedPayments = payments.filter((p: PaymentRecord) => p.id !== id);
    localStorage.setItem(this.PAYMENTS_KEY, JSON.stringify(updatedPayments));
    return true;
  }

  // Attendance - 🔧 移除缓存机制，直接从localStorage读取数据
  getAttendance(skipCache: boolean = false): AttendanceRecord[] {
    const allRecords = JSON.parse(localStorage.getItem(this.ATTENDANCE_KEY) || '[]') as AttendanceRecord[];
    
    // 直接筛选并返回结果，不使用缓存
    console.log(`[DEBUG] 直接从localStorage获取考勤数据，总数 ${allRecords.length}`);
    const result = this.filterDataByContext(allRecords, '考勤');
    console.log(`[DEBUG] 筛选后考勤数量: ${result.length}`);
    
    return result;
  }

  saveAttendance(record: AttendanceRecord) {
    // 检查是否允许修改数据
    if (!this.canModifyData(record)) {
      alert('不能修改非当前学年学期的数据');
      return false;
    }
    
    record = this.ensureContextFields(record); // 确保上下文字段存在
    
    const records = JSON.parse(localStorage.getItem(this.ATTENDANCE_KEY) || '[]');
    const index = records.findIndex((r: AttendanceRecord) => r.id === record.id);
    const currentTime = new Date().toISOString();
    
    if (index >= 0) {
      // 更新现有考勤记录，保留创建时间但更新修改时间
      record.updated_at = currentTime;
      record.last_modified_locally = currentTime;
      if (!record.created_at) {
        record.created_at = currentTime;
      }
      records[index] = record;
    } else {
      // 新建考勤记录，设置创建和修改时间
      record.created_at = currentTime;
      record.updated_at = currentTime;
      record.last_modified_locally = currentTime;
      records.push(record);
    }
    localStorage.setItem(this.ATTENDANCE_KEY, JSON.stringify(records));
    
    // 🔧 移除缓存清理，因为考勤数据不再使用缓存
    
    return true;
  }

  deleteAttendance(id: string) {
    // 先获取原始记录
    const allRecords = JSON.parse(localStorage.getItem(this.ATTENDANCE_KEY) || '[]') as AttendanceRecord[];
    const recordToDelete = allRecords.find(r => r.id === id);
    
    // 检查是否允许删除
    if (recordToDelete && !this.canModifyData(recordToDelete)) {
      alert('不能删除非当前学年学期的数据');
      return false;
    }
    
    const updatedRecords = allRecords.filter((r: AttendanceRecord) => r.id !== id);
    localStorage.setItem(this.ATTENDANCE_KEY, JSON.stringify(updatedRecords));
    return true;
  }
  
  deleteAttendanceByStudentAndDate(studentId: string, date: string) {
    // 先获取原始记录
    const allRecords = JSON.parse(localStorage.getItem(this.ATTENDANCE_KEY) || '[]') as AttendanceRecord[];
    const recordsToDelete = allRecords.filter(r => r.studentId === studentId && r.date === date);
    
    // 检查是否有非当前学年学期的记录
    const hasUnauthorizedRecords = recordsToDelete.some(record => !this.canModifyData(record));
    if (hasUnauthorizedRecords) {
      alert('包含非当前学年学期的数据，无法删除');
      return false;
    }
    
    const updatedRecords = allRecords.filter((r: AttendanceRecord) => !(r.studentId === studentId && r.date === date));
    localStorage.setItem(this.ATTENDANCE_KEY, JSON.stringify(updatedRecords));
    return true;
  }
  
  deleteAttendanceByDate(date: string) {
    // 先获取原始记录
    const allRecords = JSON.parse(localStorage.getItem(this.ATTENDANCE_KEY) || '[]') as AttendanceRecord[];
    const recordsToDelete = allRecords.filter(r => r.date === date);
    
    // 检查是否有非当前学年学期的记录
    const hasUnauthorizedRecords = recordsToDelete.some(record => !this.canModifyData(record));
    if (hasUnauthorizedRecords) {
      alert('包含非当前学年学期的数据，无法删除');
      return false;
    }
    
    const updatedRecords = allRecords.filter((r: AttendanceRecord) => r.date !== date);
    localStorage.setItem(this.ATTENDANCE_KEY, JSON.stringify(updatedRecords));
    return true;
  }
  
  batchDeleteAttendanceByStudentsAndDate(studentIds: string[], date: string) {
    // 先获取原始记录
    const allRecords = JSON.parse(localStorage.getItem(this.ATTENDANCE_KEY) || '[]') as AttendanceRecord[];
    const recordsToDelete = allRecords.filter(r => studentIds.includes(r.studentId) && r.date === date);
    
    // 检查是否有非当前学年学期的记录
    const hasUnauthorizedRecords = recordsToDelete.some(record => !this.canModifyData(record));
    if (hasUnauthorizedRecords) {
      alert('包含非当前学年学期的数据，无法删除');
      return false;
    }
    
    const updatedRecords = allRecords.filter((r: AttendanceRecord) => !(studentIds.includes(r.studentId) && r.date === date));
    localStorage.setItem(this.ATTENDANCE_KEY, JSON.stringify(updatedRecords));
    return true;
  }

  // Grades
  getGrades(skipCache: boolean = false): GradeRecord[] {
    const allGrades = JSON.parse(localStorage.getItem(this.GRADES_KEY) || '[]') as GradeRecord[];
    
    // 如果请求跳过缓存，直接筛选并返回结果
    if (skipCache) {
      console.log(`[DEBUG] 跳过缓存，直接筛选成绩数据`);
      const result = this.filterDataByContext(allGrades, '成绩');
      console.log(`[DEBUG] 直接筛选后成绩数量: ${result.length}`);
      return result;
    }
    
    // 使用缓存优化频繁的成绩数据获取
    const cacheKey = `${this.currentSchoolYearId}-${this.currentTermId}-grades`;
    const cachedResult = sessionStorage.getItem(cacheKey);
    
    if (cachedResult) {
      try {
        const parsed = JSON.parse(cachedResult);
        console.log(`[DEBUG] 从缓存获取成绩数据: ${parsed.length}条`);
        return parsed;
      } catch (e) {
        console.error('成绩数据缓存解析失败:', e);
        // 如果解析失败，继续使用原始方法
      }
    }
    
    // 如果没有缓存，使用原来的过滤方法
    const result = this.filterDataByContext(allGrades, '成绩');
    console.log(`[DEBUG] 未找到缓存，筛选后成绩数量: ${result.length}`);
    
    // 缓存结果到 sessionStorage (仅缓存当前会话)
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify(result));
    } catch (e) {
      console.error('缓存成绩数据失败:', e);
      // 缓存失败不影响主要功能，只记录错误
    }
    
    return result;
  }

  saveGrade(grade: GradeRecord) {
    // 检查是否允许修改数据
    if (!this.canModifyData(grade)) {
      alert('不能修改非当前学年学期的数据');
      return false;
    }
    
    grade = this.ensureContextFields(grade); // 确保上下文字段存在
    
    const grades = JSON.parse(localStorage.getItem(this.GRADES_KEY) || '[]') as GradeRecord[];
    const index = grades.findIndex((g: GradeRecord) => g.id === grade.id);
    const currentTime = new Date().toISOString();
    
    if (index >= 0) {
      // 更新现有成绩记录，保留创建时间但更新修改时间
      grade.updated_at = currentTime;
      grade.last_modified_locally = currentTime;
      if (!grade.created_at) {
        grade.created_at = currentTime;
      }
      grades[index] = grade;
    } else {
      // 新建成绩记录，设置创建和修改时间
      grade.created_at = currentTime;
      grade.updated_at = currentTime;
      grade.last_modified_locally = currentTime;
      grades.push(grade);
    }
    localStorage.setItem(this.GRADES_KEY, JSON.stringify(grades));
    return true;
  }

  deleteGrade(id: string) {
    // 先获取原始记录
    const allGrades = JSON.parse(localStorage.getItem(this.GRADES_KEY) || '[]') as GradeRecord[];
    const gradeToDelete = allGrades.find(g => g.id === id);
    
    // 检查是否允许删除
    if (gradeToDelete && !this.canModifyData(gradeToDelete)) {
      alert('不能删除非当前学年学期的数据');
      return false;
    }
    
    const updatedGrades = allGrades.filter((g: GradeRecord) => g.id !== id);
    localStorage.setItem(this.GRADES_KEY, JSON.stringify(updatedGrades));
    return true;
  }

  // Classes
  getClasses(): ClassInfo[] {
    const allClasses = JSON.parse(localStorage.getItem(this.CLASSES_KEY) || '[]') as ClassInfo[];
    
    // 使用缓存优化频繁的班级数据获取
    const cacheKey = `${this.currentSchoolYearId}-${this.currentTermId}-classes`;
    const cachedResult = sessionStorage.getItem(cacheKey);
    
    if (cachedResult) {
      try {
        return JSON.parse(cachedResult);
      } catch (e) {
        console.error('班级数据缓存解析失败:', e);
        // 如果解析失败，继续使用原始方法
      }
    }
    
    // 如果没有缓存，使用原来的过滤方法
    const result = this.filterDataByContext(allClasses, '班级');
    
    // 缓存结果到 sessionStorage
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify(result));
    } catch (e) {
      console.error('缓存班级数据失败:', e);
    }
    
    return result;
  }

  saveClass(classInfo: ClassInfo) {
    // 检查是否允许修改数据
    if (!this.canModifyData(classInfo)) {
      alert('不能修改非当前学年学期的数据');
      return false;
    }
    
    classInfo = this.ensureContextFields(classInfo); // 确保上下文字段存在
    
    const classes = JSON.parse(localStorage.getItem(this.CLASSES_KEY) || '[]') as ClassInfo[];
    const index = classes.findIndex((c: ClassInfo) => c.id === classInfo.id);
    const currentTime = new Date().toISOString();
    
    if (index >= 0) {
      // 更新现有班级，保留创建时间但更新修改时间
      classInfo.updated_at = currentTime;
      classInfo.last_modified_locally = currentTime;
      if (!classInfo.created_at) {
        classInfo.created_at = currentTime;
      }
      classes[index] = classInfo;
    } else {
      // 新建班级，设置创建和修改时间
      classInfo.created_at = currentTime;
      classInfo.updated_at = currentTime;
      classInfo.last_modified_locally = currentTime;
      classes.push(classInfo);
    }
    localStorage.setItem(this.CLASSES_KEY, JSON.stringify(classes));
    
    // 清除相关缓存
    this.clearEntityCache('classes');
    
    return true;
  }

  deleteClass(id: string) {
    // 先获取原始记录
    const allClasses = JSON.parse(localStorage.getItem(this.CLASSES_KEY) || '[]') as ClassInfo[];
    const classToDelete = allClasses.find(c => c.id === id);
    
    // 检查是否允许删除
    if (classToDelete && !this.canModifyData(classToDelete)) {
      alert('不能删除非当前学年学期的数据');
      return false;
    }
    
    const updatedClasses = allClasses.filter((c: ClassInfo) => c.id !== id);
    localStorage.setItem(this.CLASSES_KEY, JSON.stringify(updatedClasses));
    
    // 清除相关缓存
    this.clearEntityCache('classes');
    
    return true;
  }

  // Subjects
  getSubjects(): Subject[] {
    const allSubjects = JSON.parse(localStorage.getItem(this.SUBJECTS_KEY) || '[]') as Subject[];
    
    // 使用缓存优化频繁的科目数据获取
    const cacheKey = `${this.currentSchoolYearId}-${this.currentTermId}-subjects`;
    const cachedResult = sessionStorage.getItem(cacheKey);
    
    if (cachedResult) {
      try {
        return JSON.parse(cachedResult);
      } catch (e) {
        console.error('科目数据缓存解析失败:', e);
        // 如果解析失败，继续使用原始方法
      }
    }
    
    // 如果没有缓存，使用原来的过滤方法
    const result = this.filterDataByContext(allSubjects, '科目');
    
    // 缓存结果到 sessionStorage
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify(result));
    } catch (e) {
      console.error('缓存科目数据失败:', e);
    }
    
    return result;
  }

  saveSubject(subject: Subject) {
    // 检查是否允许修改数据
    if (!this.canModifyData(subject)) {
      alert('不能修改非当前学年学期的数据');
      return false;
    }
    
    subject = this.ensureContextFields(subject); // 确保上下文字段存在
    
    const subjects = JSON.parse(localStorage.getItem(this.SUBJECTS_KEY) || '[]') as Subject[];
    const index = subjects.findIndex((s: Subject) => s.id === subject.id);
    const currentTime = new Date().toISOString();
    
    if (index >= 0) {
      // 更新现有科目，保留创建时间但更新修改时间
      subject.updated_at = currentTime;
      subject.last_modified_locally = currentTime;
      if (!subject.created_at) {
        subject.created_at = currentTime;
      }
      subjects[index] = subject;
    } else {
      // 新建科目，设置创建和修改时间
      subject.created_at = currentTime;
      subject.updated_at = currentTime;
      subject.last_modified_locally = currentTime;
      subjects.push(subject);
    }
    localStorage.setItem(this.SUBJECTS_KEY, JSON.stringify(subjects));
    
    // 清除相关缓存
    this.clearEntityCache('subjects');
    
    return true;
  }

  deleteSubject(id: string) {
    // 先获取原始记录
    const allSubjects = JSON.parse(localStorage.getItem(this.SUBJECTS_KEY) || '[]') as Subject[];
    const subjectToDelete = allSubjects.find(s => s.id === id);
    
    // 检查是否允许删除
    if (subjectToDelete && !this.canModifyData(subjectToDelete)) {
      alert('不能删除非当前学年学期的数据');
      return false;
    }
    
    const updatedSubjects = allSubjects.filter((s: Subject) => s.id !== id);
    localStorage.setItem(this.SUBJECTS_KEY, JSON.stringify(updatedSubjects));
    
    // 清除相关缓存
    this.clearEntityCache('subjects');
    
    return true;
  }

  getSubjectsByClass(classId: string): Subject[] {
    return this.getSubjects().filter(subject => 
      subject.classIds.includes(classId) && subject.isActive
    );
  }

  // Custom Reports
  getCustomReports(): any[] {
    return JSON.parse(localStorage.getItem(this.CUSTOM_REPORTS_KEY) || '[]');
  }

  saveCustomReport(report: any) {
    const reports = this.getCustomReports();
    const index = reports.findIndex(r => r.id === report.id);
    if (index >= 0) {
      reports[index] = report;
    } else {
      reports.push(report);
    }
    localStorage.setItem(this.CUSTOM_REPORTS_KEY, JSON.stringify(reports));
  }

  deleteCustomReport(id: string) {
    const reports = this.getCustomReports().filter(r => r.id !== id);
    localStorage.setItem(this.CUSTOM_REPORTS_KEY, JSON.stringify(reports));
  }

  // Alert Rules
  getAlertRules(): any[] {
    return JSON.parse(localStorage.getItem(this.ALERT_RULES_KEY) || '[]');
  }

  saveAlertRule(rule: any) {
    const rules = this.getAlertRules();
    const index = rules.findIndex(r => r.id === rule.id);
    if (index >= 0) {
      rules[index] = rule;
    } else {
      rules.push(rule);
    }
    localStorage.setItem(this.ALERT_RULES_KEY, JSON.stringify(rules));
  }

  deleteAlertRule(id: string) {
    const rules = this.getAlertRules().filter(r => r.id !== id);
    localStorage.setItem(this.ALERT_RULES_KEY, JSON.stringify(rules));
  }

  // User Preferences
  getPreferences(): Record<string, any> {
    return JSON.parse(localStorage.getItem(this.PREFERENCES_KEY) || '{}');
  }

  savePreference(key: string, value: any) {
    const preferences = this.getPreferences();
    preferences[key] = value;
    localStorage.setItem(this.PREFERENCES_KEY, JSON.stringify(preferences));
  }

  getPreference(key: string, defaultValue: any = null) {
    const preferences = this.getPreferences();
    return preferences[key] !== undefined ? preferences[key] : defaultValue;
  }

  // Utility methods
  getStudentsByClass(classId: string): Student[] {
    // 使用已经筛选过的学生数据
    return this.getStudents().filter(student => student.classId === classId);
  }

  getStudentPayments(studentId: string): PaymentRecord[] {
    return this.getPayments().filter(payment => payment.studentId === studentId);
  }

  getStudentAttendance(studentId: string): AttendanceRecord[] {
    return this.getAttendance().filter(record => record.studentId === studentId);
  }

  getStudentGrades(studentId: string): GradeRecord[] {
    return this.getGrades().filter(grade => grade.studentId === studentId);
  }
  
  // 数据迁移工具 - 为旧数据添加学年学期标记
  migrateDataToCurrentContext() {
    if (!this.hasSchoolYearContext()) {
      console.error('无法迁移数据：没有有效的学年上下文');
      return {
        success: false,
        stats: {
          students: 0,
          payments: 0,
          attendance: 0,
          grades: 0,
          classes: 0,
          subjects: 0,
          total: 0
        }
      };
    }
    
    try {
      // 迁移学生记录
      const students = JSON.parse(localStorage.getItem(this.STUDENTS_KEY) || '[]') as Student[];
      const unassignedStudents = students.filter((student: Student) => !student.schoolYearId);
      const updatedStudents = students.map((student: Student) => {
        if (!student.schoolYearId) {
          const newStudent = { ...student, schoolYearId: this.currentSchoolYearId };
          // 如果有学期上下文，也设置学期
          if (this.currentTermId) {
            newStudent.termId = this.currentTermId;
          }
          return newStudent;
        }
        return student;
      });
      localStorage.setItem(this.STUDENTS_KEY, JSON.stringify(updatedStudents));
      
      // 迁移支付记录
      const payments = JSON.parse(localStorage.getItem(this.PAYMENTS_KEY) || '[]') as PaymentRecord[];
      const unassignedPayments = payments.filter((payment: PaymentRecord) => !payment.schoolYearId);
      const updatedPayments = payments.map((payment: PaymentRecord) => {
        if (!payment.schoolYearId) {
          const newPayment = { ...payment, schoolYearId: this.currentSchoolYearId };
          // 如果有学期上下文，也设置学期
          if (this.currentTermId) {
            newPayment.termId = this.currentTermId;
          }
          return newPayment;
        }
        return payment;
      });
      localStorage.setItem(this.PAYMENTS_KEY, JSON.stringify(updatedPayments));
      
      // 迁移考勤记录 (类似逻辑)
      const attendance = JSON.parse(localStorage.getItem(this.ATTENDANCE_KEY) || '[]') as AttendanceRecord[];
      const unassignedAttendance = attendance.filter((record: AttendanceRecord) => !record.schoolYearId);
      const updatedAttendance = attendance.map((record: AttendanceRecord) => {
        if (!record.schoolYearId) {
          const newRecord = { ...record, schoolYearId: this.currentSchoolYearId };
          if (this.currentTermId) {
            newRecord.termId = this.currentTermId;
          }
          return newRecord;
        }
        return record;
      });
      localStorage.setItem(this.ATTENDANCE_KEY, JSON.stringify(updatedAttendance));
      
      // 迁移成绩记录 (类似逻辑)
      const grades = JSON.parse(localStorage.getItem(this.GRADES_KEY) || '[]') as GradeRecord[];
      const unassignedGrades = grades.filter((grade: GradeRecord) => !grade.schoolYearId);
      const updatedGrades = grades.map((grade: GradeRecord) => {
        if (!grade.schoolYearId) {
          const newGrade = { ...grade, schoolYearId: this.currentSchoolYearId };
          if (this.currentTermId) {
            newGrade.termId = this.currentTermId;
          }
          return newGrade;
        }
        return grade;
      });
      localStorage.setItem(this.GRADES_KEY, JSON.stringify(updatedGrades));
      
      // 迁移班级信息 (类似逻辑)
      const classes = JSON.parse(localStorage.getItem(this.CLASSES_KEY) || '[]') as ClassInfo[];
      const unassignedClasses = classes.filter((classInfo: ClassInfo) => !classInfo.schoolYearId);
      const updatedClasses = classes.map((classInfo: ClassInfo) => {
        if (!classInfo.schoolYearId) {
          const newClass = { ...classInfo, schoolYearId: this.currentSchoolYearId };
          if (this.currentTermId) {
            newClass.termId = this.currentTermId;
          }
          return newClass;
        }
        return classInfo;
      });
      localStorage.setItem(this.CLASSES_KEY, JSON.stringify(updatedClasses));
      
      // 迁移科目信息 (类似逻辑)
      const subjects = JSON.parse(localStorage.getItem(this.SUBJECTS_KEY) || '[]') as Subject[];
      const unassignedSubjects = subjects.filter((subject: Subject) => !subject.schoolYearId);
      const updatedSubjects = subjects.map((subject: Subject) => {
        if (!subject.schoolYearId) {
          const newSubject = { ...subject, schoolYearId: this.currentSchoolYearId };
          if (this.currentTermId) {
            newSubject.termId = this.currentTermId;
          }
          return newSubject;
        }
        return subject;
      });
      localStorage.setItem(this.SUBJECTS_KEY, JSON.stringify(updatedSubjects));
      
      // 迁移教师考勤记录
      const teacherAttendance = JSON.parse(localStorage.getItem(this.TEACHER_ATTENDANCE_KEY) || '[]') as TeacherAttendanceRecord[];
      const unassignedTeacherAttendance = teacherAttendance.filter((record: TeacherAttendanceRecord) => !record.schoolYearId);
      const updatedTeacherAttendance = teacherAttendance.map((record: TeacherAttendanceRecord) => {
        if (!record.schoolYearId) {
          const newRecord = { ...record, schoolYearId: this.currentSchoolYearId };
          if (this.currentTermId) {
            newRecord.termId = this.currentTermId;
          }
          return newRecord;
        }
        return record;
      });
      localStorage.setItem(this.TEACHER_ATTENDANCE_KEY, JSON.stringify(updatedTeacherAttendance));
      
      // 迁移教师记录
      const teachers = JSON.parse(localStorage.getItem(this.TEACHERS_KEY) || '[]') as Teacher[];
      const unassignedTeachers = teachers.filter((teacher: Teacher) => !teacher.schoolYearId);
      const updatedTeachers = teachers.map((teacher: Teacher) => {
        if (!teacher.schoolYearId) {
          const newTeacher = { ...teacher, schoolYearId: this.currentSchoolYearId };
          if (this.currentTermId) {
            newTeacher.termId = this.currentTermId;
          }
          return newTeacher;
        }
        return teacher;
      });
      localStorage.setItem(this.TEACHERS_KEY, JSON.stringify(updatedTeachers));
      
      // 计算总数
      const totalMigrated = 
        unassignedStudents.length +
        unassignedPayments.length + 
        unassignedAttendance.length + 
        unassignedGrades.length + 
        unassignedClasses.length + 
        unassignedSubjects.length + 
        unassignedTeacherAttendance.length + 
        unassignedTeachers.length;
      
      // 🔥 修复：数据迁移完成后，清除所有相关实体的缓存
      // 这样考勤管理等功能就能立即看到迁移过来的数据
      if (totalMigrated > 0) {
        console.log('[DataMigration] 数据迁移完成，清除相关缓存以确保数据立即可见');
        this.clearEntityCache('students');
        this.clearEntityCache('payments');
        // 🔧 attendance已移除缓存机制，无需清除
        this.clearEntityCache('grades');
        this.clearEntityCache('classes');
        this.clearEntityCache('subjects');
        this.clearEntityCache('teacher_attendance');
        this.clearEntityCache('teachers');
        console.log('[DataMigration] 缓存清除完成');
      }
      
      // 返回迁移统计
      return {
        success: true,
        stats: {
          students: unassignedStudents.length,
          payments: unassignedPayments.length,
          attendance: unassignedAttendance.length,
          grades: unassignedGrades.length,
          classes: unassignedClasses.length,
          subjects: unassignedSubjects.length,
          teacherAttendance: unassignedTeacherAttendance.length,
          teachers: unassignedTeachers.length,
          total: totalMigrated
        }
      };
    } catch (error) {
      console.error('数据迁移失败:', error);
      return {
        success: false,
        stats: {
          students: 0,
          payments: 0,
          attendance: 0,
          grades: 0,
          classes: 0,
          subjects: 0,
          teacherAttendance: 0,
          teachers: 0,
          total: 0
        }
      };
    }
  }

  // 判断是否允许修改数据
  canModifyData(record?: {schoolYearId?: string, termId?: string}): boolean {
    // 检查是否有任何学年数据，如果没有，允许创建（初始化系统使用）
    const schoolYears = this.getSchoolYears();
    if (schoolYears.length === 0) {
      console.log('[DataProtection] 系统中没有学年数据，允许创建基础数据');
      return true;
    }
    
    // 如果没有设置任何上下文（学年或学期），不允许修改数据
    if (!this.currentSchoolYearId) {
      console.warn('[DataProtection] 未设置当前学年，不能修改数据');
      return false;
    }
    
    // 如果未设置学期，不允许修改数据
    if (!this.currentTermId) {
      console.warn('[DataProtection] 未设置当前学期，不能修改数据');
      return false;
    }
    
    // 检查选择的学期是否是活动学期
    const currentYear = schoolYears.find(y => y.id === this.currentSchoolYearId);
    
    if (currentYear && currentYear.terms) {
      const currentTerm = currentYear.terms.find(t => t.id === this.currentTermId);
      
      // 如果当前选择的学期不是活动学期，不允许修改数据
      if (currentTerm && !currentTerm.isActive) {
        console.warn(`[DataProtection] 当前选择的学期 (${currentTerm.name}) 不是活动学期，不能修改数据`);
        return false;
      }
    }
    
    // 如果是新记录（没有传入记录参数），必须设置了完整上下文（学年和学期）
    if (!record) {
      // 创建新记录必须有完整上下文
      const canCreate = this.hasValidContext();
      if (!canCreate) {
        console.warn('[DataProtection] 未设置完整上下文（学年和学期），不能创建新数据');
      }
      return canCreate;
    }
    
    // 检查记录的学年是否与当前上下文匹配
    if (record.schoolYearId && record.schoolYearId !== this.currentSchoolYearId) {
      console.warn(`[DataProtection] 记录属于不同学年 (${record.schoolYearId})，不能在当前学年 (${this.currentSchoolYearId}) 下修改`);
      return false;
    }
    
    // 检查记录的学期是否与当前上下文匹配
    if (record.termId && record.termId !== this.currentTermId) {
      console.warn(`[DataProtection] 记录属于不同学期 (${record.termId})，只能在对应学期下修改`);
      return false;
    }
    
    return true;
  }

  // Teachers
  getTeachers(): Teacher[] {
    const allTeachers = JSON.parse(localStorage.getItem(this.TEACHERS_KEY) || '[]') as Teacher[];
    
    console.log(`[DEBUG] 获取教师记录：总数 ${allTeachers.length}`);
    console.log(`[DEBUG] 当前上下文: schoolYearId=${this.currentSchoolYearId}, termId=${this.currentTermId}`);
    
    return this.filterDataByContext(allTeachers, '教师');
  }

  saveTeacher(teacher: Teacher): boolean {
    // 如果教师没有学年学期标记，但有当前上下文，则添加关联
    if ((!teacher.schoolYearId || !teacher.termId) && this.hasValidContext()) {
      teacher.schoolYearId = this.currentSchoolYearId || undefined;
      teacher.termId = this.currentTermId || undefined;
    }
    
    // 检查是否有权限修改数据
    if (!this.canModifyData(teacher)) {
      console.error('[StorageManager] 保存教师失败：没有权限修改非当前学年学期的数据');
      return false;
    }
    
    const teachers = JSON.parse(localStorage.getItem(this.TEACHERS_KEY) || '[]') as Teacher[];
    const index = teachers.findIndex(t => t.id === teacher.id);
    const currentTime = new Date().toISOString();
    
    if (index >= 0) {
      // 更新现有教师，保留创建时间但更新修改时间
      teacher.updated_at = currentTime;
      teacher.last_modified_locally = currentTime;
      if (!teacher.created_at) {
        teacher.created_at = currentTime;
      }
      teachers[index] = teacher;
    } else {
      // 新建教师，设置创建和修改时间
      teacher.created_at = currentTime;
      teacher.updated_at = currentTime;
      teacher.last_modified_locally = currentTime;
      teachers.push(teacher);
    }
    localStorage.setItem(this.TEACHERS_KEY, JSON.stringify(teachers));
    
    // 清除相关缓存
    this.clearEntityCache('teachers');
    
    return true;
  }
  
  deleteTeacher(teacherId: string): boolean {
    // 🔥 修复：从全局数据中获取，确保正确操作
    const allTeachers = JSON.parse(localStorage.getItem(this.TEACHERS_KEY) || '[]') as Teacher[];
    const teacherToDelete = allTeachers.find(t => t.id === teacherId);
    
    // 检查是否有权限修改数据
    if (!teacherToDelete || !this.canModifyData(teacherToDelete)) {
      console.error('[StorageManager] 删除教师失败：没有权限修改非当前学年学期的数据');
      return false;
    }
    
    // 检查教师是否关联到任何班级（只检查当前上下文的班级）
    const classes = this.getClasses();
    const isTeacherAssigned = classes.some(c => c.teacher === teacherToDelete.name);
    if (isTeacherAssigned) {
      console.error('[StorageManager] 删除教师失败：该教师已关联到一个或多个班级');
      return false;
    }
    
    // 🔥 修复：从全局数据中删除，保留其他学年学期的教师数据
    const updatedAllTeachers = allTeachers.filter(t => t.id !== teacherId);
    localStorage.setItem(this.TEACHERS_KEY, JSON.stringify(updatedAllTeachers));
    
    // 清除相关缓存
    this.clearEntityCache('teachers');
    
    console.log(`[StorageManager] 成功删除教师: ${teacherToDelete.name} (ID: ${teacherId})`);
    console.log(`[StorageManager] 剩余教师总数: ${updatedAllTeachers.length}`);
    
    return true;
  }
  
  getActiveTeachers(): Teacher[] {
    return this.getTeachers().filter(teacher => teacher.status === 'active');
  }

  // 教师考勤相关方法
  getTeacherAttendance(skipCache: boolean = false): TeacherAttendanceRecord[] {
    const allRecords = JSON.parse(localStorage.getItem(this.TEACHER_ATTENDANCE_KEY) || '[]') as TeacherAttendanceRecord[];
    
    console.log(`[DEBUG] 获取教师考勤记录：总数 ${allRecords.length}`);
    console.log(`[DEBUG] 当前上下文: schoolYearId=${this.currentSchoolYearId}, termId=${this.currentTermId}`);
    
    return this.filterDataByContext(allRecords, '教师考勤');
  }

  saveTeacherAttendance(record: TeacherAttendanceRecord): boolean {
    // 检查是否允许修改数据
    if (!this.canModifyData(record)) {
      alert('不能修改非当前学年学期的数据');
      return false;
    }
    
    record = this.ensureContextFields(record); // 确保上下文字段存在
    
    const records = JSON.parse(localStorage.getItem(this.TEACHER_ATTENDANCE_KEY) || '[]');
    const index = records.findIndex((r: TeacherAttendanceRecord) => r.id === record.id);
    const currentTime = new Date().toISOString();
    
    if (index >= 0) {
      // 更新现有教师考勤记录，保留创建时间但更新修改时间
      record.updated_at = currentTime;
      record.last_modified_locally = currentTime;
      if (!record.created_at) {
        record.created_at = currentTime;
      }
      records[index] = record;
    } else {
      // 新建教师考勤记录，设置创建和修改时间
      record.created_at = currentTime;
      record.updated_at = currentTime;
      record.last_modified_locally = currentTime;
      records.push(record);
    }
    localStorage.setItem(this.TEACHER_ATTENDANCE_KEY, JSON.stringify(records));
    
    // 清除相关缓存
    this.clearEntityCache('teacher_attendance');
    
    return true;
  }

  deleteTeacherAttendance(id: string): boolean {
    const records = JSON.parse(localStorage.getItem(this.TEACHER_ATTENDANCE_KEY) || '[]') as TeacherAttendanceRecord[];
    const recordToDelete = records.find(r => r.id === id);
    
    // 检查是否允许修改数据
    if (!recordToDelete || !this.canModifyData(recordToDelete)) {
      alert('不能修改非当前学年学期的数据');
      return false;
    }
    
    const updatedRecords = records.filter(r => r.id !== id);
    localStorage.setItem(this.TEACHER_ATTENDANCE_KEY, JSON.stringify(updatedRecords));
    
    // 清除相关缓存
    this.clearEntityCache('teacher_attendance');
    
    return true;
  }

  deleteTeacherAttendanceByTeacherAndDate(teacherId: string, date: string): boolean {
    const records = JSON.parse(localStorage.getItem(this.TEACHER_ATTENDANCE_KEY) || '[]') as TeacherAttendanceRecord[];
    const recordsToDelete = records.filter(r => r.teacherId === teacherId && r.date === date);
    
    // 检查是否允许修改数据
    if (recordsToDelete.some(r => !this.canModifyData(r))) {
      alert('不能修改非当前学年学期的数据');
      return false;
    }
    
    const updatedRecords = records.filter(r => !(r.teacherId === teacherId && r.date === date));
    localStorage.setItem(this.TEACHER_ATTENDANCE_KEY, JSON.stringify(updatedRecords));
    
    // 清除相关缓存
    this.clearEntityCache('teacher_attendance');
    
    return true;
  }

  deleteTeacherAttendanceByDate(date: string): boolean {
    const records = JSON.parse(localStorage.getItem(this.TEACHER_ATTENDANCE_KEY) || '[]') as TeacherAttendanceRecord[];
    const recordsToDelete = records.filter(r => r.date === date);
    
    // 检查是否允许修改数据
    if (recordsToDelete.some(r => !this.canModifyData(r))) {
      alert('不能修改非当前学年学期的数据');
      return false;
    }
    
    const updatedRecords = records.filter(r => r.date !== date);
    localStorage.setItem(this.TEACHER_ATTENDANCE_KEY, JSON.stringify(updatedRecords));
    
    // 清除相关缓存
    this.clearEntityCache('teacher_attendance');
    
    return true;
  }

  batchDeleteTeacherAttendanceByTeachersAndDate(teacherIds: string[], date: string): boolean {
    const records = JSON.parse(localStorage.getItem(this.TEACHER_ATTENDANCE_KEY) || '[]') as TeacherAttendanceRecord[];
    const recordsToDelete = records.filter(r => teacherIds.includes(r.teacherId) && r.date === date);
    
    // 检查是否允许修改数据
    if (recordsToDelete.some(r => !this.canModifyData(r))) {
      alert('不能修改非当前学年学期的数据');
      return false;
    }
    
    const updatedRecords = records.filter(r => !(teacherIds.includes(r.teacherId) && r.date === date));
    localStorage.setItem(this.TEACHER_ATTENDANCE_KEY, JSON.stringify(updatedRecords));
    
    // 清除相关缓存
    this.clearEntityCache('teacher_attendance');
    
    return true;
  }

  getTeacherAttendanceByTeacher(teacherId: string): TeacherAttendanceRecord[] {
    return this.getTeacherAttendance().filter(record => record.teacherId === teacherId);
  }

  // 获取自定义考试列表
  getCustomExams(): {name: string, date: string, schoolYearId?: string, termId?: string}[] {
    // 从localStorage中获取自定义考试列表
    const allCustomExams = JSON.parse(localStorage.getItem(this.CUSTOM_EXAMS_KEY) || '[]');
    
    // 过滤出当前学年学期的考试
    return this.filterDataByContext(allCustomExams, '自定义考试');
  }

  // 保存自定义考试列表
  saveCustomExams(customExams: {name: string, date: string}[]) {
    // 确保每个考试都有上下文字段
    const examsWithContext = customExams.map(exam => this.ensureContextFields({
      ...exam,
      id: exam.name // 使用考试名称作为ID
    }));
    
    // 获取所有自定义考试
    const allCustomExams = JSON.parse(localStorage.getItem(this.CUSTOM_EXAMS_KEY) || '[]');
    
    // 移除当前上下文的自定义考试
    const otherCustomExams = allCustomExams.filter((exam: any) => {
      return exam.schoolYearId !== this.currentSchoolYearId || exam.termId !== this.currentTermId;
    });
    
    // 合并并保存
    const updatedCustomExams = [...otherCustomExams, ...examsWithContext];
    localStorage.setItem(this.CUSTOM_EXAMS_KEY, JSON.stringify(updatedCustomExams));
    
    return true;
  }

  // 在适当的位置，添加缓存清理方法
  clearEntityCache(entityType: string) {
    // 🔧 学生考勤已移除缓存机制，无需清除
    if (entityType === 'attendance') {
      console.log(`跳过清除${entityType}缓存：已移除缓存机制`);
      return;
    }
    
    // 清除特定实体的缓存
    const cacheKey = `${this.currentSchoolYearId}-${this.currentTermId}-${entityType}`;
    try {
      console.log(`清除${entityType}缓存: ${cacheKey}`);
      sessionStorage.removeItem(cacheKey);
    } catch (e) {
      console.error(`清除${entityType}缓存失败:`, e);
    }
  }
  
  // 清除所有会话缓存（保留UI状态和记忆功能）
  clearAllCache() {
    try {
      console.log('清除数据缓存，保留UI状态记忆...');
      
      // 🔧 选择性清除缓存：只清除数据缓存，保留UI状态记忆
      // 需要清除的数据实体缓存（不包括attendance，因为已移除缓存机制）
      const dataEntities = ['students', 'classes', 'grades', 'payments', 'subjects', 'schoolYears', 'teachers', 'teacher_attendance'];
      
      // 清除特定数据实体的缓存
      dataEntities.forEach(entity => {
        const cacheKey = `${this.currentSchoolYearId}-${this.currentTermId}-${entity}`;
        try {
          sessionStorage.removeItem(cacheKey);
          console.log(`已清除${entity}缓存`);
        } catch (e) {
          console.warn(`清除${entity}缓存失败:`, e);
        }
      });
      
      // 保留以下类型的缓存：
      // - UI状态缓存（标签选择、下拉菜单状态等）
      // - preferences相关缓存
      // - 用户界面记忆功能
      console.log('✅ 数据缓存清除完成，UI状态记忆已保留');
      return true;
    } catch (e) {
      console.error('清除缓存失败:', e);
      return false;
    }
  }
}

export const storageManager = new StorageManager();